import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { Loading, Card, PageHeader, EmptyState } from "@/components/common";
import { toast } from "sonner";
import { Newspaper, ExternalLink, Printer, CheckCircle2 } from "lucide-react";

const RANGES = [["today", "Today"], ["24h", "Last 24 Hours"], ["since_last", "Since Last Brief"]];

const SECTIONS = [
  ["priority_legislative_changes", "Priority Legislative Changes"],
  ["new_amendments", "New Amendments"],
  ["recent_votes", "Recent Votes"],
  ["status_changes", "Status Changes"],
  ["upcoming_meetings", "Upcoming Meetings"],
  ["bills_requiring_review", "Bills Requiring Review"],
  ["watchlist_matches", "Watchlist Matches"],
  ["governor_developments", "Governor Developments"],
  ["agency_developments", "Agency Developments"],
  ["rec_budget_developments", "REC / Budget Developments"],
  ["court_developments", "Court Developments"],
];

function Item({ it }) {
  const ext = it.source_url ? (it.source_url.startsWith("http") ? it.source_url : `https://${it.source_url}`) : "";
  return (
    <div className="py-2.5 border-b border-brand-line last:border-0" data-testid="brief-item">
      <div className="flex items-center gap-2 flex-wrap">
        {it.identifier && <span className="font-mono text-xs font-semibold text-brand-civic">{it.identifier}</span>}
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 uppercase font-bold">{(it.event_type || "").replace(/_/g, " ")}</span>
        {it.date && <span className="text-[10px] text-slate-400">{fmtDate(it.date)}</span>}
      </div>
      <p className="text-sm text-brand-ink font-medium mt-0.5">{it.title}</p>
      {it.description && <p className="text-xs text-slate-500 mt-0.5">{it.description}</p>}
      <div className="flex items-center gap-3 mt-1">
        {it.record_link && <Link to={it.record_link} className="text-xs text-brand-civic hover:underline">Open record →</Link>}
        {ext && <a href={ext} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-brand-civic"><ExternalLink className="h-3 w-3" /> Source</a>}
      </div>
    </div>
  );
}

export default function Brief() {
  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);
  const load = (r) => { setData(null); api.get(`/brief?range=${r}`).then((res) => setData(res.data)).catch(() => setData(false)); };
  useEffect(() => { load(range); }, [range]);
  const checkpoint = async () => { try { await api.post("/brief/checkpoint"); toast.success("Marked as reviewed — 'Since Last Brief' reset to now"); if (range === "since_last") load(range); } catch { toast.error("Failed to update"); } };

  return (
    <div>
      <PageHeader title="Executive Brief" subtitle="Deterministic, source-grounded summary of legislative activity. No AI, no inferred positions."
        actions={
          <div className="flex items-center gap-2">
            <button onClick={checkpoint} data-testid="brief-checkpoint" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-brand-line bg-white text-sm text-brand-navy font-medium hover:border-brand-civic/50"><CheckCircle2 className="h-4 w-4" /> Mark reviewed</button>
            <button onClick={() => window.print()} data-testid="brief-print" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-brand-navy text-white text-sm font-semibold hover:bg-brand-navy-700"><Printer className="h-4 w-4" /> Print</button>
          </div>
        } />

      <div className="flex items-center gap-1 mb-6 bg-white border border-brand-line rounded-lg p-1 w-fit" data-testid="brief-range">
        {RANGES.map(([v, l]) => (
          <button key={v} onClick={() => setRange(v)} data-testid={`range-${v}`}
            className={`px-3 h-8 rounded-md text-sm font-medium transition-colors ${range === v ? "bg-brand-navy text-white" : "text-slate-500 hover:text-brand-navy"}`}>{l}</button>
        ))}
      </div>

      {data === null ? <Loading /> : !data ? <EmptyState icon={Newspaper} title="Unable to load brief" /> : (
        <div data-testid="brief-content">
          <p className="text-xs text-slate-400 mb-4">Generated {fmtDate(data.generated_at)} · window since {fmtDate(data.since)} · {data.data_mode?.toUpperCase()} data</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {SECTIONS.map(([key, label]) => {
              const items = data.sections[key] || [];
              return (
                <Card key={key} data-testid={`brief-section-${key}`}>
                  <h3 className="font-display font-semibold text-brand-ink text-sm mb-1 flex items-center justify-between">{label}<span className="text-xs font-bold text-slate-400">{items.length}</span></h3>
                  {items.length ? items.map((it, i) => <Item key={i} it={it} />) : <p className="text-sm text-slate-400 py-2">Nothing in this window.</p>}
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
